import { type ComboboxItem } from "@mantine/core";
import { PaymentMethod } from "../components/DeliveryOverview/DeliveryOverview.types";
import { getTheme } from "../mantine/themeConfig";
import { InvoiceState } from "./backend-types";
import { Frequency, Medium, ObjectName, PhysicalSize, Unit } from "./enums";

export const theme =
  localStorage.getItem("node_theme") ||
  document.documentElement.getAttribute("data-theme")?.trim() ||
  "default";

export const THEME_VARS = getTheme(theme);

export const FREQUENCY_OPTIONS = {
  [Frequency.TenMinutes]: "10 Minuten",
  [Frequency.QuarterHour]: "Viertelstunde",
  [Frequency.Hour]: "Stunde",
  [Frequency.Day]: "Tag",
  [Frequency.Month]: "Monat",
  [Frequency.Year]: "Jahr"
} as const;

export const FREQUENCY_CHOICES = {
  TEN_MINUTES: {
    value: Frequency.TenMinutes,
    displayName: FREQUENCY_OPTIONS[Frequency.TenMinutes]
  },
  QUARTER_HOUR: {
    value: Frequency.QuarterHour,
    displayName: FREQUENCY_OPTIONS[Frequency.QuarterHour]
  },
  HOUR: {
    value: Frequency.Hour,
    displayName: FREQUENCY_OPTIONS[Frequency.Hour]
  },
  DAY: { value: Frequency.Day, displayName: FREQUENCY_OPTIONS[Frequency.Day] },
  MONTH: {
    value: Frequency.Month,
    displayName: FREQUENCY_OPTIONS[Frequency.Month]
  },
  YEAR: {
    value: Frequency.Year,
    displayName: FREQUENCY_OPTIONS[Frequency.Year]
  }
} as const;

export const MEDIUM_LABEL_MAP = {
  [Medium.Electricity]: "Strom",
  [Medium.Gas]: "Gas",
  [Medium.Heat]: "Wärme"
} satisfies Record<Medium, string>;

export const MEDIUM_CHOICES: ReadonlyArray<ComboboxItem> = Object.entries(
  MEDIUM_LABEL_MAP
).map(([value, label]) => ({ value, label }));

export const UNITS = {
  [Unit.Watt]: {
    value: Unit.Watt,
    displayName: "W (Watt)",
    physicalSize: PhysicalSize.Power
  },
  [Unit.WattHour]: {
    value: Unit.WattHour,
    displayName: "Wh (Wattstunden)",
    physicalSize: PhysicalSize.Energy
  },
  [Unit.Kilowatt]: {
    value: Unit.Kilowatt,
    displayName: "kW (Kilowatt)",
    physicalSize: PhysicalSize.Power
  },
  [Unit.KilowattHour]: {
    value: Unit.KilowattHour,
    displayName: "kWh (Kilowattstunden)",
    physicalSize: PhysicalSize.Energy
  },
  [Unit.Megawatt]: {
    value: Unit.Megawatt,
    displayName: "MW (Megawatt)",
    physicalSize: PhysicalSize.Power
  },
  [Unit.MegawattHour]: {
    value: Unit.MegawattHour,
    displayName: "MWh (Megawattstunden)",
    physicalSize: PhysicalSize.Energy
  },
  [Unit.CubicMeter]: {
    value: Unit.CubicMeter,
    displayName: "m³ (Kubikmeter)",
    physicalSize: PhysicalSize.Volume
  }
};

export const INVOICE_STATE_CHOICES: Array<ComboboxItem> = [
  {
    label: "Versandfertig",
    value: InvoiceState.ReadyToSend
  },
  {
    label: "Versendet",
    value: InvoiceState.Sent
  },
  {
    label: "Bezahlt",
    value: InvoiceState.Paid
  }
];

export const PAYMENT_METHOD_CHOICES: Array<ComboboxItem> = [
  {
    value: PaymentMethod.BankTransfer,
    label: "Überweisung"
  },
  {
    value: PaymentMethod.DirectDebit,
    label: "Lastschrift"
  }
];

export const COMPONENT_QUERY_KEY: Readonly<Record<ObjectName, string>> = {
  [ObjectName.Generator]: "generators",
  [ObjectName.Consumer]: "consumers",
  [ObjectName.Storage]: "storages",
  [ObjectName.Person]: "persons",
  [ObjectName.Connection]: "connections",
  [ObjectName.GasConnection]: "gasConnections",
  [ObjectName.Meter]: "meters",
  [ObjectName.GhostNode]: "ghostNodes",
  [ObjectName.MarketLocation]: "marketLocations",
  [ObjectName.MeteringLocation]: "meteringLocations",
  [ObjectName.MeteringOrMarketLocation]: "meteringOrMarketLocations",
  [ObjectName.Weighting]: "weightings"
};

export const EMPTY_FIELD = "--";
export const UNNAMED_OBJECT_NAME = "Nicht benannt";

export const CREATE_STEPS = {
  IMPORT_OR_CREATE: 1,
  CREATE_FORM: 2,
  ANLAGE_TAX_FORM: 3,
  BUSINESS_RULES_FORM: 4,

  CONFIRMATION_PAGE: 6,
  MASTR_IMPORT_CONFIRMATION_PAGE: 7,
  GAS_CONNECTION_DATA: 8
} as const;

export const emptyArray = [];

export const MIETERSTROMZUSCHLAG_FORM = {
  APPLICATION_INTENT_LABEL:
    "Mieterstromzuschlag möglich – soll opti.node die relevanten Unterlagen und Energiemengen bereitstellen?",
  APPLICATION_INTENT_HELP_TEXT:
    "Die von Ihnen registrierten Anlagen kommen für die Belieferung gewerblicher Kunden in Betracht.",
  APPLICATION_DATE_LABEL:
    "Datum der Anmeldung des Mieterstromzuschlags beim Netzbetreiber",
  APPLY_OR_ALREADY_APPLIED_LABEL:
    "Ja, ich möchte den Mieterstromzuschlag beantragen / habe den Mieterstromzuschlag bereits beantragt",
  DO_NOT_APPLY_LABEL: "Nein, interessiert mich nicht"
} as const;
